import type { EventKind } from "../styles/theme";

export type IsoDate = string;
export type IsoDateTime = string;

export type ApiError = { error: string; details?: unknown };

export class ApiRequestError extends Error {
  status: number;
  body: ApiError | undefined;
  constructor(status: number, body: ApiError | undefined, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

// Trailing slash is stripped so callers always pass absolute paths like `/api/...`.
// Empty string = same-origin (dev via Vite proxy, prod via same domain).
const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = (body ?? {}) as ApiError;
    throw new ApiRequestError(res.status, err, err.error ?? `http_${res.status}`);
  }
  return body as T;
}

// ----- Types --------------------------------------------------------
export type EventRow = {
  id: string;
  date: IsoDate;
  day_number: number | null;
  tray_id: string | null;
  crop: string | null;
  kind: EventKind;
  text: string;
  delivery: 0 | 1;
  plan_id: string | null;
  cycle_id: string | null;
  completed_at: IsoDateTime | null;
  created_at: IsoDateTime;
};

export type EventCreate = {
  date: IsoDate;
  day_number?: number | null;
  tray_id?: string | null;
  crop?: string | null;
  kind: EventKind;
  text: string;
  delivery?: boolean;
  plan_id?: string | null;
  cycle_id?: string | null;
  completed?: boolean;
};

export type HarvestRow = {
  id: string;
  event_id: string | null;
  date: IsoDate;
  tray_id: string | null;
  crop: string;
  seed_oz: number;
  yield_oz: number | null;
  germination_pct: number | null;
  notes: string | null;
  created_at: IsoDateTime;
  packed_at: IsoDateTime | null;
};

export type HarvestCreate = {
  event_id?: string | null;
  date: IsoDate;
  tray_id?: string | null;
  crop: string;
  seed_oz: number;
  yield_oz?: number | null;
  germination_pct?: number | null;
  notes?: string | null;
};

export type HarvestPatch = Partial<HarvestCreate>;

export type BurnDown = {
  avg_weekly_oz: number | null;
  days_until_empty: number | null;
};

export type StockRow = {
  id: string;
  crop: string;
  source: string | null;
  size: string | null;
  size_oz: number | null;
  remaining_oz: number | null;
  germ_pct: number | null;
  germ_date: IsoDate | null;
  notes: string | null;
  burn_down: BurnDown;
};

export type ReferenceRow = {
  crop: string;
  seed_oz_min: number | null;
  seed_oz_max: number | null;
  soak_hours: number;
  blackout_days: number;
  harvest_days_min: number;
  harvest_days_max: number;
  yield_oz_min: number | null;
  yield_oz_max: number | null;
  weighted_blackout: number;
};

export type PlanRow = {
  id: string;
  name: string;
  start_date: IsoDate;
  end_date: IsoDate;
  notes: string | null;
  created_at: IsoDateTime;
};

export type DeliveryTarget = "tuesday" | "friday";

export type PlanTrayInput = {
  tray_id: string;
  crop: string;
  delivery_target: DeliveryTarget;
  tray_fraction?: TrayFraction;
  seed_oz?: number | null;
  blackout_days?: number | null;
  harvest_days?: number | null;
};

export type PlanRotationInput = {
  tray_id: string;
  switch_on_date: IsoDate;
  new_crop: string;
};

export type PlanGenerateBody = {
  start_date: IsoDate;
  duration_days: number;
  trays: PlanTrayInput[];
  rotations?: PlanRotationInput[];
};

export type RecommendedEvent = {
  date: IsoDate;
  day_number: number;
  tray_id: string | null;
  crop: string | null;
  kind: EventKind;
  text: string;
};

export type AgedSeedWarning = {
  kind: "aged_seed";
  tray_id: string;
  crop: string;
  reason: string;
  germ_pct: number;
  original_seed_oz: number;
  bumped_seed_oz: number;
};

export type SeedBeforePlanStartWarning = {
  kind: "seed_before_plan_start";
  tray_id: string;
  crop: string;
  seed_date: IsoDate;
  start_date: IsoDate;
  days_before_start: number;
};

export type GeneratorWarning = AgedSeedWarning | SeedBeforePlanStartWarning;

export type PlanGenerateResponse = {
  plan_id: string;
  start_date: IsoDate;
  duration_days: number;
  recommended_events: RecommendedEvent[];
  warnings: GeneratorWarning[];
};

export type PlanApplyResponse = {
  cycles_created: number;
  events_created: number;
  warnings: GeneratorWarning[];
};

export type PlanApplyConflict = {
  cycle_id: string;
  tray_id: string;
  crop: string;
  tray_name: string | null;
};

export type PlanAlreadyAppliedCycle = {
  id: string;
  tray_id: string;
  crop: string;
};

export type PlanCreate = {
  name: string;
  start_date: IsoDate;
  end_date: IsoDate;
  notes?: string | null;
};

export type CyclePhase = "planned" | "soaking" | "blackout" | "growing" | "harvested";

// BRO-699: only these four fractions are valid. The whole-tray default is 1.
export type TrayFraction = 1 | 0.5 | 0.25 | 0.125;
export const TRAY_FRACTION_VALUES: readonly TrayFraction[] = [1, 0.5, 0.25, 0.125] as const;

export function formatTrayFraction(fraction: number): string {
  if (fraction >= 0.999) return "1";
  if (Math.abs(fraction - 0.5) < 1e-6) return "1/2";
  if (Math.abs(fraction - 0.25) < 1e-6) return "1/4";
  if (Math.abs(fraction - 0.125) < 1e-6) return "1/8";
  return fraction.toString();
}

export type CycleRow = {
  id: string;
  tray_id: string;
  crop: string;
  tray_fraction: number;
  started_at: IsoDateTime;
  soak_started_at: IsoDateTime | null;
  seeded_at: IsoDateTime | null;
  uncovered_at: IsoDateTime | null;
  harvested_at: IsoDateTime | null;
  target_delivery_date: IsoDate | null;
  plan_id: string | null;
  notes: string | null;
  phase: CyclePhase;
};

export type CycleCreate = {
  tray_id: string;
  crop: string;
  tray_fraction?: TrayFraction;
  started_at?: IsoDateTime;
  soak_started_at?: IsoDateTime | null;
  seeded_at?: IsoDateTime | null;
  uncovered_at?: IsoDateTime | null;
  harvested_at?: IsoDateTime | null;
  target_delivery_date?: IsoDate | null;
  plan_id?: string | null;
  notes?: string | null;
};

export type TrayFractionConflict = {
  cycle_id: string;
  crop: string;
  fraction: number;
};

export type TrayFractionOverflow = {
  tray_id: string;
  occupied: number;
  requested: number;
  conflicts: TrayFractionConflict[];
};

export type DeliveryItem = {
  harvest_id: string;
  event_id: string | null;
  tray_id: string;
  crop: string;
  tray_fraction: number;
  seed_oz: number;
  yield_oz: number | null;
  packed_at: IsoDateTime | null;
};

export type DeliveryGroup = { delivery_date: IsoDate; items: DeliveryItem[] };

// ----- Endpoints ----------------------------------------------------
export const api = {
  health: () => request<{ ok: boolean; phase: string }>("/api/health"),

  listEventsByDate: (date: IsoDate) =>
    request<EventRow[]>(`/api/events?date=${encodeURIComponent(date)}`),
  listEventsByRange: (from: IsoDate, to: IsoDate) =>
    request<EventRow[]>(
      `/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  createEvent: (input: EventCreate) =>
    request<EventRow>("/api/events", { method: "POST", body: JSON.stringify(input) }),
  patchEvent: (id: string, patch: Partial<EventCreate>) =>
    request<EventRow>(`/api/events/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  completeEvent: (id: string, completed: boolean) =>
    request<EventRow>(`/api/events/${id}/complete`, {
      method: "PATCH",
      body: JSON.stringify({ completed }),
    }),
  deleteEvent: (id: string) => request<void>(`/api/events/${id}`, { method: "DELETE" }),

  listHarvests: (crop?: string) =>
    request<HarvestRow[]>(
      crop ? `/api/harvests?crop=${encodeURIComponent(crop)}` : "/api/harvests",
    ),
  createHarvest: (input: HarvestCreate) =>
    request<HarvestRow>("/api/harvests", { method: "POST", body: JSON.stringify(input) }),
  patchHarvest: (id: string, patch: HarvestPatch) =>
    request<HarvestRow>(`/api/harvests/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  packHarvest: (id: string, packed: boolean) =>
    request<HarvestRow>(`/api/harvests/${id}/pack`, {
      method: "PATCH",
      body: JSON.stringify({ packed }),
    }),
  deleteHarvest: (id: string) => request<void>(`/api/harvests/${id}`, { method: "DELETE" }),

  listStock: () => request<StockRow[]>("/api/stock"),
  patchStock: (id: string, patch: Partial<Omit<StockRow, "id">>) =>
    request<StockRow>(`/api/stock/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  listReference: () => request<ReferenceRow[]>("/api/reference"),

  listPlans: () => request<PlanRow[]>("/api/plans"),
  createPlan: (input: PlanCreate) =>
    request<PlanRow>("/api/plans", { method: "POST", body: JSON.stringify(input) }),
  deletePlan: (id: string) =>
    request<{ deleted: boolean; events_deleted: number; cycles_deleted: number }>(
      `/api/plans/${id}`,
      { method: "DELETE" },
    ),
  generatePlan: (id: string, body: PlanGenerateBody) =>
    request<PlanGenerateResponse>(`/api/plans/${id}/generate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  applyPlan: (id: string, body: PlanGenerateBody) =>
    request<PlanApplyResponse>(`/api/plans/${id}/apply`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listCycles: (filter: { tray_id?: string; active?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (filter.tray_id) params.set("tray_id", filter.tray_id);
    if (filter.active !== undefined) params.set("active", String(filter.active));
    const qs = params.toString();
    return request<CycleRow[]>(qs ? `/api/cycles?${qs}` : "/api/cycles");
  },
  createCycle: (input: CycleCreate) =>
    request<CycleRow>("/api/cycles", { method: "POST", body: JSON.stringify(input) }),
  patchCycle: (id: string, patch: Partial<CycleCreate>) =>
    request<CycleRow>(`/api/cycles/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteCycle: (id: string) => request<void>(`/api/cycles/${id}`, { method: "DELETE" }),

  listDeliveries: (from: IsoDate, to: IsoDate) =>
    request<DeliveryGroup[]>(
      `/api/deliveries?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
};
